/**
 * Hand-drawn geometry.
 *
 * Every wobble here is a pure function of a string seed. `Math.random()` would
 * hand the server one squiggle and the client another, and React would tear the
 * page down with a hydration mismatch — so the same seed always draws the same
 * line, on both sides, forever.
 *
 * Strokes are drawn twice with different jitter, which is what makes a line read
 * as drawn rather than merely crooked: a person going over an edge never lands
 * on their first pass.
 */

/** FNV-1a into mulberry32 — small, fast, and stable across runtimes. */
function seeded(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

type Point = [number, number];

/** One pass of a wobbly line: bowed at the middle, and never quite on target. */
function pass(a: Point, b: Point, rand: () => number, bow: number): string {
  const jitter = (n: number) => (rand() - 0.5) * n;
  const [x1, y1] = [a[0] + jitter(bow), a[1] + jitter(bow)];
  const [x2, y2] = [b[0] + jitter(bow), b[1] + jitter(bow)];

  // Push the control point off the line's midpoint, perpendicular to it, so the
  // stroke bows the way a hand bows rather than kinking.
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const off = (rand() - 0.5) * bow * 1.8;
  const cx = (x1 + x2) / 2 + (-dy / len) * off;
  const cy = (y1 + y2) / 2 + (dx / len) * off;

  return `M${x1.toFixed(2)} ${y1.toFixed(2)} Q${cx.toFixed(2)} ${cy.toFixed(2)} ${x2.toFixed(2)} ${y2.toFixed(2)}`;
}

/** A line, drawn twice. */
export function roughLine(a: Point, b: Point, seed: string, bow = 2): string[] {
  const rand = seeded(seed);
  return [pass(a, b, rand, bow), pass(a, b, rand, bow * 0.8)];
}

/**
 * A rectangle whose corners do not quite meet — the overshoot is what stops it
 * reading as a CSS border with a filter on it.
 */
export function roughRect(w: number, h: number, seed: string, bow = 2.4): string[] {
  const rand = seeded(seed);
  const over = () => (rand() - 0.2) * bow * 1.6;
  const corners: Point[] = [
    [0, 0],
    [w, 0],
    [w, h],
    [0, h],
  ];

  const paths: string[] = [];
  for (let i = 0; i < 4; i++) {
    const from = corners[i];
    const to = corners[(i + 1) % 4];
    // Run each edge slightly past its corner, the way a pen does.
    const dx = Math.sign(to[0] - from[0]);
    const dy = Math.sign(to[1] - from[1]);
    const start: Point = [from[0] - dx * over(), from[1] - dy * over()];
    const end: Point = [to[0] + dx * over(), to[1] + dy * over()];
    paths.push(pass(start, end, rand, bow), pass(start, end, rand, bow * 0.7));
  }
  return paths;
}

/** An ellipse in one loose sweep, closed past where it started. */
export function roughEllipse(w: number, h: number, seed: string, bow = 3): string[] {
  const rand = seeded(seed);
  const rx = w / 2;
  const ry = h / 2;
  const cx = rx;
  const cy = ry;

  const sweep = (from: number, to: number, wobble: number) => {
    const steps = 12;
    const pts: string[] = [];
    for (let i = 0; i <= steps; i++) {
      const t = from + ((to - from) * i) / steps;
      const j = (rand() - 0.5) * wobble;
      const x = cx + Math.cos(t) * (rx + j);
      const y = cy + Math.sin(t) * (ry + j);
      pts.push(`${i === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)}`);
    }
    return pts.join(" ");
  };

  // Just over a full turn, twice, so the ends cross rather than butt together.
  return [sweep(0, Math.PI * 2.12, bow), sweep(0.4, Math.PI * 2.3, bow * 0.7)];
}

/** A curved arrow with a two-stroke head, for pointing at things. */
export function roughArrow(a: Point, b: Point, seed: string, curve = 0.35): string[] {
  const rand = seeded(seed);
  const [x1, y1] = a;
  const [x2, y2] = b;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;

  const cx = (x1 + x2) / 2 + -dy * curve;
  const cy = (y1 + y2) / 2 + dx * curve;
  const shaft = `M${x1} ${y1} Q${cx.toFixed(2)} ${cy.toFixed(2)} ${x2} ${y2}`;

  // Head angled off the tangent coming out of the control point, so it points
  // where the curve arrives rather than where the straight line would have.
  const angle = Math.atan2(y2 - cy, x2 - cx);
  const head = 9 + rand() * 3;
  const spread = 0.44;
  const h1: Point = [x2 - Math.cos(angle - spread) * head, y2 - Math.sin(angle - spread) * head];
  const h2: Point = [x2 - Math.cos(angle + spread) * head, y2 - Math.sin(angle + spread) * head];

  return [
    shaft,
    `M${x2} ${y2} L${h1[0].toFixed(2)} ${h1[1].toFixed(2)}`,
    `M${x2} ${y2} L${h2[0].toFixed(2)} ${h2[1].toFixed(2)}`,
    len > 40 ? pass([x1, y1], [x2, y2], rand, 1.2) : "",
  ].filter(Boolean);
}

/** Two scratchy strokes under a word. */
export function roughUnderline(w: number, seed: string): string[] {
  const rand = seeded(seed);
  const dip = 2 + rand() * 2;
  return [
    `M2 ${dip} Q${(w / 2).toFixed(1)} ${(dip + 3.4).toFixed(1)} ${(w - 2).toFixed(1)} ${(dip - 0.6).toFixed(1)}`,
    `M4 ${(dip + 3).toFixed(1)} Q${(w / 2).toFixed(1)} ${(dip + 6).toFixed(1)} ${(w - 5).toFixed(1)} ${(dip + 2).toFixed(1)}`,
  ];
}
